import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { guard } from 'lit/directives/guard.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';

import type { TeamId } from '../../../../game/types/Players';
import { flagSvgPath } from '../../../../services/Flags';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { scrollbarThinHover } from '../../../theme/scrollbar.css';
import { tokens } from '../../../theme/tokens.css';
import type {
  CtfFlagAction,
  GameLogBtrWinEntry,
  GameLogCtfFlagEntry,
  GameLogEntry,
  GameLogJoinEntry,
  GameLogKillEntry,
  GameLogLeaveEntry,
  GameLogPlayer,
  GameLogPlayersAliveEntry,
} from '../../../UIState';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { GAME_LOG_MIN_HEIGHT, GAME_LOG_TOP, gameLogAvailableHeight, gameLogFits } from '../layout';

/** Team IDs as used across the codebase. 0 = FFA / unteamed. */
const TEAM_BLUE = 1;
const TEAM_RED = 2;

/** Accent class per CTF action. The producer never logs "taken" (too noisy),
 *  but the record covers the whole union, so a new action won't compile
 *  until it's given one. */
const CTF_EVENT_CLASS: Record<CtfFlagAction, string> = {
  captured: 'ev-cap',
  returned: 'ev-ret',
  taken: '',
};

/**
 * Game Log — kill feed panel.
 *
 * Renders the rolling list of combat events (kills, joins, leaves, CTF flag
 * actions, BTR results) in the top-left of the screen. Auto-scrolls to the
 * bottom on new entries when the user is already at the bottom; if scrolled
 * up, surfaces a "new entries" chip the user can tap to jump back down.
 *
 * @element game-log
 */
@customElement('game-log')
export class GameLog extends LitElement {
  static override styles = [
    tokens,
    scrollbarThinHover,
    css`
      :host {
        display: block;
        position: absolute;
        /* + the status-bar inset, in step with the top bar (see airmash-game).
           It's 0 on desktop, so layout.ts's fit math needn't know it. */
        top: calc(${GAME_LOG_TOP}px + env(safe-area-inset-top, 0px));
        /* Clear of the landscape notch; 0 on non-notched screens. */
        left: calc(12px + env(safe-area-inset-left, 0px));
        width: fit-content;
        max-width: 500px;
        /* Declared here rather than inherited from the body. No selection
           (a double-click on a name would select it) or long-press callout. */
        font-family: var(--font-family);
        font-size: var(--font-size-13);
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        box-sizing: border-box;
        pointer-events: none;
        z-index: 5;
      }

      /* Mobile never shows the log (gameLogFits), but a touch-first computer
         does, and its toolbar grows to 44px chips, dropping their bottom to
         ~58px. Nudge the log below them. Visual only — layout.ts's fit math
         keeps the desktop 55px. */
      @media (hover: none) {
        :host {
          top: calc(64px + env(safe-area-inset-top, 0px));
        }
      }

      .log-container {
        display: flex;
        flex-direction: column;
        gap: 2px;
        /* auto, not scroll — gutter only appears when content actually
           overflows, otherwise it eats 6px of width forever. */
        overflow-y: auto;
        pointer-events: auto;
      }

      .entry {
        position: relative;
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 2px 6px;
        background: rgba(0, 0, 0, 0.1);
        border-radius: var(--radius-sm);
        text-shadow: var(--shadow-text);
        animation: fadeIn 0.2s ease-out;
        overflow: hidden;
        flex-shrink: 0;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Kills are the log's noise. After 3s they settle to half strength,
         so joins, leaves, captures and returns stay the brightest lines,
         and a capture is still easy to find in a busy fight. */
      .entry.kill {
        animation:
          fadeIn 0.2s ease-out,
          settle 0.8s ease 3s forwards;
      }

      @keyframes settle {
        to {
          opacity: 0.5;
        }
      }

      /* The events players act on: they greet a join, notice a leave, and
         want to know who capped or returned. Each gets one sweep of light in
         its colour as it arrives, then only its colours and icon mark it.
         Returns take silver, the partner to capture's gold; a team colour
         would read as a team, and the flag glyph already says whose flag.
         The icon greens and reds are lifted from #5a5 / #a55 to read at
         12px over the map. */
      .ev-join {
        --ev: #5a5;
        --ev-icon: #78c878;
      }

      .ev-leave {
        --ev: #a55;
        --ev-icon: #e27d7d;
      }

      .ev-cap {
        --ev: var(--color-accent-gold);
      }

      .ev-ret {
        --ev: var(--color-text-bright);
      }

      .entry.key::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          100deg,
          transparent 25%,
          color-mix(in srgb, var(--ev) 55%, transparent) 50%,
          transparent 75%
        );
        transform: translateX(-110%);
        animation: glint 0.9s ease-out 0.15s forwards;
        pointer-events: none;
      }

      @keyframes glint {
        to {
          transform: translateX(110%);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .entry.key::after {
          display: none;
        }
      }

      /* Join / leave arrow. The mask sits on ::before so this element's
         drop-shadow survives it (a filter runs before the element's own
         mask, which would clip the shadow away). */
      .glyph {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
        filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9));
      }

      .glyph::before {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        background-color: var(--ev-icon);
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      .glyph.join::before {
        mask-image: url('/vanilla/ui/log/log-in.svg');
        -webkit-mask-image: url('/vanilla/ui/log/log-in.svg');
      }

      .glyph.leave::before {
        mask-image: url('/vanilla/ui/log/log-out.svg');
        -webkit-mask-image: url('/vanilla/ui/log/log-out.svg');
      }

      .player {
        display: inline-flex;
        align-items: center;
        gap: 3px;
      }

      .player.killer {
        /* Cap killer/joiner/leaver names so a single long handle can't
           dominate the row. The inner .name already has overflow:ellipsis;
           constraining the wrapping flex item is what activates it. */
        min-width: 0;
        flex: 0 1 auto;
        max-width: 220px;
        overflow: hidden;
      }

      .player.victim {
        min-width: 0;
        flex: 1 1 0;
        overflow: hidden;
      }

      .player.clickable {
        pointer-events: auto;
        cursor: pointer;
        border-radius: var(--radius-xs);
        padding: 0 3px;
        margin: 0 -3px;
      }

      @media (hover: hover) {
        .player.clickable:hover {
          background-color: var(--color-overlay-subtle);
        }
      }

      .flag {
        width: 16px;
        height: 11px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        flex-shrink: 0;
      }

      .name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--color-text-link);
        font-weight: bold;
      }

      .name.blue {
        color: var(--color-team-blue);
      }

      .name.red {
        color: var(--color-team-red);
      }

      .action {
        color: #999;
        margin: 0 2px;
        flex-shrink: 0;
      }

      .action.joined {
        color: #5a5;
        font-weight: bold;
      }

      .action.left {
        color: #a55;
        font-weight: bold;
      }

      .action.captured {
        color: var(--color-accent-gold);
        font-weight: bold;
      }

      .action.returned {
        color: var(--color-text-bright);
        font-weight: bold;
      }

      /* CTF flag glyph — recoloured via mask-image so we don't need the
         filter-cocktail hack that hue-rotates a generic SVG. Matches the
         pattern used in debug-panel / dev-test-panel icons. */
      .ctf-flag {
        width: 14px;
        height: 14px;
        flex-shrink: 0;
        display: inline-block;
        mask-image: url('/vanilla/ui/scoreboard/flag.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-image: url('/vanilla/ui/scoreboard/flag.svg');
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      .ctf-flag.blue {
        background-color: var(--color-team-blue);
      }

      .ctf-flag.red {
        background-color: var(--color-team-red);
      }

      .ctf-text {
        color: #999;
      }

      .ctf-team.blue {
        color: var(--color-team-blue);
      }

      .ctf-team.red {
        color: var(--color-team-red);
      }

      .btr-win {
        color: var(--color-accent-gold);
        font-weight: bold;
        min-width: 0;
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .btr-trophy {
        display: inline-block;
        margin-right: 4px;
      }

      .players-alive {
        color: #aaa;
        font-style: italic;
      }

      .players-alive-count {
        color: var(--color-text-white);
        font-weight: bold;
        font-style: normal;
      }

      /* "New entries below" chip — appears when the user has scrolled up
         and new entries have arrived since. Sticky so it pins to the visible
         bottom of the scroll container even when the actual chip element is
         further down the scrolled content. Tap to jump to the latest. */
      .unread-chip {
        position: sticky;
        bottom: 4px;
        z-index: 1;
        align-self: flex-start;
        margin-top: 4px;
        padding: 3px 8px;
        background: rgba(0, 0, 0, 0.6);
        border: 1px solid var(--color-overlay-medium);
        border-radius: 10px;
        color: var(--color-text-white);
        font-size: var(--font-size-xs);
        font-weight: bold;
        font-family: inherit;
        cursor: pointer;
        pointer-events: auto;
        text-shadow: var(--shadow-text);
      }

      @media (hover: hover) {
        .unread-chip:hover {
          background: var(--color-bg-selector-hover);
        }
      }
    `,
  ];

  private state = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private api!: GameAPIType;

  private containerRef: Ref<HTMLElement> = createRef();

  // Scroll tracking: decide before render, scroll after
  private prevLastEntryKey: number | null = null;
  private scrollToLatest = false;

  @state() private unreadCount = 0;

  private isScrolledToBottom(container: HTMLElement) {
    return container.scrollHeight - container.scrollTop - container.clientHeight < 10;
  }

  /** Works out, before Lit applies DOM changes, whether this update follows
   *  new entries to the bottom or counts them as unread. It runs here rather
   *  than in updated() because unreadCount is reactive: set in willUpdate it
   *  joins this update, set in updated() it scheduled a second one (Lit's
   *  change-in-update warning). */
  override willUpdate() {
    this.scrollToLatest = false;
    const entries = this.state.gameLogEntries;
    // Mirror render()'s visibility predicate so unread/prev state resets in
    // every "log isn't on screen" case (entries empty, setting toggled off,
    // viewport too cramped) — not just empty-entries. Without this, ghost
    // "N new" can survive a setting toggle.
    const logVisible = this.state.gameSettings.gameLog && gameLogFits(this.state) && entries.length > 0;

    if (!logVisible) {
      this.unreadCount = 0;
      this.prevLastEntryKey = null;
      return;
    }

    // Nothing appended, nothing to decide. This re-renders on every uiState
    // change (ping, chat, zoom drags), and the scroll read below forces a
    // layout, so skip it unless the newest entry changed.
    const lastEntryKey = entries[entries.length - 1]!.id;
    if (lastEntryKey === this.prevLastEntryKey) return;

    // Count how many entries were appended in this update. Valtio can batch
    // multiple pushes into one render, so a naive `+= 1` undercounts when
    // events arrive in bursts.
    const addedCount = this.countNewEntries(entries, this.prevLastEntryKey);
    this.prevLastEntryKey = lastEntryKey;

    // No container yet means the log is appearing: start at the newest.
    const container = this.containerRef.value;
    if (!container || this.isScrolledToBottom(container)) {
      this.scrollToLatest = true;
      this.unreadCount = 0;
    } else {
      this.unreadCount += addedCount;
    }
  }

  override updated() {
    if (!this.scrollToLatest) return;
    const container = this.containerRef.value;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    // Second pass after layout settles — rare but real on some browsers.
    requestAnimationFrame(() => {
      const c = this.containerRef.value;
      if (c) c.scrollTop = c.scrollHeight;
    });
  }

  /** Number of entries appended since `previousLastId`. Returns the full
   *  list length if we have no prior anchor or the anchor's gone from the
   *  list (capped off the front). */
  private countNewEntries(entries: readonly GameLogEntry[], previousLastId: number | null): number {
    if (entries.length === 0) return 0;
    if (previousLastId === null) return entries.length;
    const previousIndex = entries.findIndex((entry) => entry.id === previousLastId);
    if (previousIndex === -1) return entries.length;
    return entries.length - previousIndex - 1;
  }

  override render() {
    // Off if the setting is disabled, on mobile, or when there's no vertical
    // room (the sidebar's `vh/2 - 290` crashes into the log's 80px floor on
    // short laptop windows). The corresponding setting in menu-drawer uses
    // the same predicate, so users never see a toggle for a control that has
    // no effect.
    if (!this.state.gameSettings.gameLog || !gameLogFits(this.state)) {
      return nothing;
    }

    const entries = this.state.gameLogEntries;
    if (entries.length === 0) {
      return nothing;
    }

    // gameLogFits guarantees availableHeight >= GAME_LOG_MIN_HEIGHT above;
    // Math.max is belt-and-suspenders against a borderline race during resize.
    const maxHeight = Math.max(GAME_LOG_MIN_HEIGHT, gameLogAvailableHeight(this.state.viewport));

    return html`
      <div
        class="log-container scrollbar-thin-hover"
        style=${styleMap({ maxHeight: `${maxHeight}px` })}
        @scroll=${this.handleScroll}
        ${ref(this.containerRef)}
      >
        ${repeat(
          entries,
          (entry) => entry.id,
          // An entry is never changed in place (GameLog pushes and shifts), so its template depends on the
          // entry alone and is kept across the re-render every uiState write brings, as the chat keeps its lines
          (entry) => guard([entry], () => this.renderEntry(entry)),
        )}
        ${
          this.unreadCount > 0
            ? html`<button class="unread-chip" @click=${this.handleUnreadClick}>${this.unreadCount} new ▼</button>`
            : nothing
        }
      </div>
    `;
  }

  private renderEntry(entry: GameLogEntry) {
    switch (entry.type) {
      case 'kill':
        return this.renderKillEntry(entry);
      case 'join':
        return this.renderJoinEntry(entry);
      case 'leave':
        return this.renderLeaveEntry(entry);
      case 'ctf-flag':
        return this.renderCtfFlagEntry(entry);
      case 'btr-win':
        return this.renderBtrWinEntry(entry);
      case 'players-alive':
        return this.renderPlayersAliveEntry(entry);
      default: {
        // Exhaustiveness guard — TS errors here if a new entry type is added
        // to GameLogEntry without a matching case.
        const _exhaustive: never = entry;
        return _exhaustive;
      }
    }
  }

  private renderKillEntry(entry: GameLogKillEntry) {
    return html`
      <div class="entry kill">
        ${this.renderPlayer(entry.killer, 'killer')}
        <span class="action">killed</span>
        ${this.renderPlayer(entry.victim, 'victim')}
      </div>
    `;
  }

  private renderJoinEntry(entry: GameLogJoinEntry) {
    return html`
      <div class="entry key ev-join">
        <span class="glyph join"></span>
        ${this.renderPlayer(entry.player, 'killer')}
        <span class="action joined">joined</span>
      </div>
    `;
  }

  private renderLeaveEntry(entry: GameLogLeaveEntry) {
    return html`
      <div class="entry key ev-leave">
        <span class="glyph leave"></span>
        ${this.renderPlayer(entry.player, 'killer')}
        <span class="action left">left</span>
      </div>
    `;
  }

  private renderCtfFlagEntry(entry: GameLogCtfFlagEntry) {
    return html`
      <div class="entry key ${CTF_EVENT_CLASS[entry.action]}">
        <span class="ctf-flag ${entry.flagTeam}"></span>
        <span class="ctf-team ${entry.flagTeam}">${entry.flagTeam === 'blue' ? 'Blue' : 'Red'} flag</span>
        <span class="action ${entry.action}">${entry.action}</span>
        <span class="ctf-text">by</span>
        <span class="name ${entry.playerTeam}">${entry.playerName}</span>
      </div>
    `;
  }

  private renderBtrWinEntry(entry: GameLogBtrWinEntry) {
    const killText = entry.kills === 1 ? 'kill' : 'kills';
    return html`
      <div class="entry">
        <span class="btr-trophy">🏆</span>
        <span class="flag" style=${styleMap({ backgroundImage: `url('${flagSvgPath(entry.flag)}')` })}></span>
        <span class="btr-win">${entry.playerName}</span>
        <span class="ctf-text">wins BTR (${entry.kills} ${killText})</span>
      </div>
    `;
  }

  private renderPlayersAliveEntry(entry: GameLogPlayersAliveEntry) {
    return html`
      <div class="entry">
        <span class="players-alive">
          <span class="players-alive-count">${entry.playersAlive}</span> players alive
        </span>
      </div>
    `;
  }

  /** Shared player chip used by kill / join / leave entries. The click /
   *  contextmenu handler is the stable `this.handlePlayerNameClick` rather
   *  than a new arrow function per entry — keeps Lit from rebinding event
   *  listeners on every re-render of the feed. Player id travels via
   *  `data-player-id` for the handler to read. */
  private renderPlayer(player: GameLogPlayer, role: 'killer' | 'victim') {
    const canClick = !player.isMe && !player.isBot;
    return html`
      <span
        class=${classMap({ player: true, [role]: true, clickable: canClick })}
        data-player-id=${canClick ? player.id : nothing}
        @click=${canClick ? this.handlePlayerNameClick : nothing}
        @contextmenu=${canClick ? this.handlePlayerNameClick : nothing}
      >
        <span class="flag" style=${styleMap({ backgroundImage: `url('${flagSvgPath(player.flag)}')` })}></span>
        <span class=${this.nameClasses(player.team)}>${player.name}</span>
      </span>
    `;
  }

  /** Opens the player context menu. Reads the player id from the element's
   *  data attribute so the handler stays identity-stable across renders. */
  private handlePlayerNameClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (e.type === 'contextmenu') e.preventDefault();
    const el = e.currentTarget;
    if (!(el instanceof HTMLElement)) return;
    const playerId = Number(el.dataset['playerId']);
    if (!Number.isFinite(playerId)) return;
    const rect = el.getBoundingClientRect();
    this.api.playClick();
    this.api.showContextMenu(playerId, rect.bottom, rect.left);
  };

  private handleUnreadClick = () => {
    const c = this.containerRef.value;
    if (!c) return;
    this.api.playClick();
    c.scrollTop = c.scrollHeight;
    this.unreadCount = 0;
  };

  /** Clears the unread chip when the user manually scrolls back to the
   *  bottom. Without this, the chip persists until the next entry arrives. */
  private handleScroll = () => {
    const c = this.containerRef.value;
    if (!c || this.unreadCount === 0) return;
    if (this.isScrolledToBottom(c)) {
      this.unreadCount = 0;
    }
  };

  /** Map team ID → name classes with team color. */
  private nameClasses(team: TeamId) {
    return classMap({ name: true, blue: team === TEAM_BLUE, red: team === TEAM_RED });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'game-log': GameLog;
  }
}
